import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';

interface TimeSlot {
  id: string; // Mongo ObjectID hex string — NOT a number
  timetable_type_id: string;
  timetable_type_name?: string; // Go's GetTimeSlots doesn't populate this;
                                 // use the timetableTypeName prop instead.
  period_number: number;
  period_label: string;
  start_time: string;
  end_time: string;
  slot_type: string;
}

interface TimeSlotFormState {
  period_number: string;
  period_label: string;
  start_time: string; // "HH:MM" — native <input type="time"> value
  end_time: string;   // "HH:MM"
  slot_type: string;
}

interface ViewTimeSlotsModalProps {
  open: boolean;
  onClose: () => void;
  timetableTypeId: string | null;
  /** Optional — pass the selected type's display label from Timetable.tsx
   * (e.g. selectedTimetableType?.label) since the API response doesn't
   * actually carry a name. Falls back to "Time Slots" if omitted. */
  timetableTypeName?: string;
  /** Optional — called after a successful add/edit/delete so the parent
   * (Timetable.tsx) can refetch its grid, since period columns depend on
   * this data. */
  onTimeSlotsChanged?: () => void;
}

// Keep in sync with the backend's `oneof=period break power_nap workbook
// activities` binding in CreateTimeSlot / UpdateTimeSlot (adminControllers).
const SLOT_TYPE_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  period:     { bg: '#eff6ff', color: '#1e40af', border: '#93c5fd' },
  break:      { bg: '#fff7ed', color: '#9a3412', border: '#fdba74' },
  power_nap:  { bg: '#f5f3ff', color: '#5b21b6', border: '#c4b5fd' },
  workbook:   { bg: '#f0fdf4', color: '#166534', border: '#86efac' },
  activities: { bg: '#fdf2f8', color: '#9d174d', border: '#f9a8d4' },
};

const SLOT_TYPE_SELECT_OPTIONS = [
  { value: 'period', label: 'Period' },
  { value: 'break', label: 'Break' },
  { value: 'power_nap', label: 'Power Nap' },
  { value: 'workbook', label: 'Workbook' },
  { value: 'activities', label: 'Activities' },
];

const getSlotTypeStyle = (slotType: string) =>
  SLOT_TYPE_COLORS[slotType] ?? { bg: '#f8fafc', color: '#374151', border: '#e5e7eb' };

const formatTime = (time: string): string => {
  if (!time) return '—';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/** Native <input type="time"> gives "HH:MM" — the API needs "HH:MM:SS". */
const toHms = (val: string) => (val ? (val.length === 5 ? `${val}:00` : val) : '');
/** Trim the API's "HH:MM:SS" down to "HH:MM" for the native time input. */
const toHm = (val: string) => (val ? val.slice(0, 5) : '');

const EMPTY_FORM: TimeSlotFormState = {
  period_number: '',
  period_label: '',
  start_time: '',
  end_time: '',
  slot_type: 'period',
};

const ViewTimeSlotsModal: React.FC<ViewTimeSlotsModalProps> = ({
  open,
  onClose,
  timetableTypeId,
  timetableTypeName,
  onTimeSlotsChanged,
}) => {
  const toast = useRef<Toast>(null);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading]     = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  // Add / edit inline panel
  const [formOpen, setFormOpen]   = useState(false);
  const [editingSlot, setEditingSlot] = useState<TimeSlot | null>(null);
  const [formData, setFormData]   = useState<TimeSlotFormState>(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);

  // Inline delete confirm
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Current session — required by the Go backend on every timeslot read
  // and write (session_id to filter/scope, session for the denormalized
  // label stored on the doc). This was missing entirely before, which is
  // why session_id never made it into the request.
  const currentSessionId = useSchoolDetailsStore((state) => state.schoolDetails?.current_session?.id);
  const currentSessionName = useSchoolDetailsStore((state) => state.schoolDetails?.current_session?.session);

  const fetchTimeSlots = useCallback(async () => {
    if (!timetableTypeId || !currentSessionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Route is `/timeslots` (see RegisterTimeSlotRoutes: r.Group("/timeslots")),
      // not `/get-time-slots`. Response key is `slots`, not `data`.
      const response = await apiClient.get('/timeslots', {
        params: {
          timetable_type_id: timetableTypeId,
          session_id: currentSessionId,
          limit: 100,
          page: 1,
        },
      });

      if (response.data.status === 'success') {
        setTimeSlots(response.data.slots || []);
      } else {
        setTimeSlots([]);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch time slots';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
      setTimeSlots([]);
    } finally {
      setLoading(false);
    }
  }, [timetableTypeId, currentSessionId]);

  useEffect(() => {
    if (open) fetchTimeSlots();
  }, [open, fetchTimeSlots]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask   = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask)   mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setTimeSlots([]);
      setLoading(true);
      setFormOpen(false);
      setEditingSlot(null);
      setConfirmDeleteId(null);
    }, 400);
  };

  // ── Add / Edit ──────────────────────────────────────────────────────────
  const openAddForm = () => {
    setEditingSlot(null);
    setFormData(EMPTY_FORM);
    setFormOpen(true);
    setConfirmDeleteId(null);
  };

  const openEditForm = (slot: TimeSlot) => {
    setEditingSlot(slot);
    setFormData({
      period_number: String(slot.period_number ?? ''),
      period_label: slot.period_label || '',
      start_time: toHm(slot.start_time),
      end_time: toHm(slot.end_time),
      slot_type: slot.slot_type || 'period',
    });
    setFormOpen(true);
    setConfirmDeleteId(null);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingSlot(null);
    setFormData(EMPTY_FORM);
  };

  const validateForm = (): string | null => {
    const num = Number(formData.period_number);
    if (!formData.period_number.trim() || isNaN(num) || num <= 0) return 'A valid period number is required.';
    if (!formData.period_label.trim()) return 'Period label is required.';
    if (formData.period_label.trim().length > 10) return 'Period label must be 10 characters or fewer.';
    if (!formData.start_time || !formData.end_time) return 'Start time and end time are required.';
    if (toHms(formData.end_time) <= toHms(formData.start_time)) return 'End time must be after start time.';
    return null;
  };

  const handleSaveForm = async () => {
    const validationError = validateForm();
    if (validationError) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: validationError, life: 4000 });
      return;
    }
    if (!timetableTypeId) return;
    if (!currentSessionId) {
      toast.current?.show({
        severity: 'error',
        summary: 'No Active Session',
        detail: 'A current session is required to save time slots.',
        life: 4000,
      });
      return;
    }

    setSaving(true);
    try {
      // timetable_type_id is a Mongo ObjectID hex string on this backend —
      // do NOT parseInt() it. session_id/session are required by
      // CreateTimeSlot's binding:"required" and were previously omitted,
      // which would 400 even once the URL is corrected.
      const payload = {
        timetable_type_id: timetableTypeId,
        session_id: currentSessionId,
        session: currentSessionName,
        period_number: Number(formData.period_number),
        period_label: formData.period_label.trim(),
        start_time: toHms(formData.start_time),
        end_time: toHms(formData.end_time),
        slot_type: formData.slot_type,
      };

      const response = editingSlot
        ? await apiClient({ method: 'PUT', url: `/timeslots/${editingSlot.id}`, data: payload })
        : await apiClient({ method: 'POST', url: '/timeslots', data: payload });

      if (response.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: editingSlot ? 'Time slot updated.' : 'Time slot added.',
          life: 3500,
        });
        closeForm();
        await fetchTimeSlots();
        onTimeSlotsChanged?.();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || `Failed to ${editingSlot ? 'update' : 'add'} time slot`,
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || `Failed to ${editingSlot ? 'update' : 'add'} time slot`,
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────
  const handleConfirmDelete = async (id: string) => {
    setDeleting(true);
    try {
      const response = await apiClient.delete(`/timeslots/${id}`);
      if (response.data.status === 'success') {
        toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Time slot deleted.', life: 3500 });
        setConfirmDeleteId(null);
        await fetchTimeSlots();
        onTimeSlotsChanged?.();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || 'Failed to delete time slot',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to delete time slot',
        life: 5000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const sortedSlots = [...timeSlots].sort((a, b) => a.period_number - b.period_number);
  const resolvedTitle = timetableTypeName || sortedSlots[0]?.timetable_type_name || 'Time Slots';

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '14px',
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: '24px',
          boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
          flexShrink: 0, border: '3px solid white',
        }}>
          <i className="pi pi-clock" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
            {resolvedTitle}
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
            {sortedSlots.length} period{sortedSlots.length === 1 ? '' : 's'} configured
          </div>
        </div>
      </div>

      {!formOpen && (
        <button
          onClick={openAddForm}
          disabled={!currentSessionId}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0,
            padding: '9px 16px', borderRadius: '10px', border: 'none',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            color: 'white', fontSize: '13px', fontWeight: 700,
            cursor: currentSessionId ? 'pointer' : 'not-allowed',
            opacity: currentSessionId ? 1 : 0.6,
            boxShadow: '0 4px 12px rgba(59,130,246,0.25)',
          }}
        >
          <i className="pi pi-plus" style={{ fontSize: '12px' }} />
          Add
        </button>
      )}
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '820px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '16px 18px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal dismissableMask draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: maskFadeIn 0.35s ease; }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes maskFadeOut { from { opacity: 1; } to { opacity: 0; } }
          .p-dialog { animation: dialogOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
          @keyframes dialogOpen { from { opacity: 0; transform: scale(0.92) translateY(-20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          @keyframes dialogClose { from { opacity: 1; transform: scale(1) translateY(0); } to { opacity: 0; transform: scale(0.88) translateY(25px); } }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241,245,249,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100,116,139,0.7); }
        `}</style>

        {!currentSessionId ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '10px' }}>
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '28px', color: '#f59e0b' }} />
            <span style={{ color: '#64748b', fontSize: '13.5px', textAlign: 'center' }}>
              No active session found. Please set a current session to manage time slots.
            </span>
          </div>
        ) : (
          <>
            {/* ── Inline Add / Edit form ── */}
            {formOpen && (
              <div style={{
                background: 'white', borderRadius: '16px', padding: '18px',
                border: '1px solid #dbeafe', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                marginBottom: '14px',
              }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '14px' }}>
                  {editingSlot ? 'Edit Time Slot' : 'New Time Slot'}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
                      Period Number *
                    </label>
                    <input
                      type="number"
                      value={formData.period_number}
                      onChange={(e) => setFormData({ ...formData, period_number: e.target.value })}
                      disabled={saving}
                      placeholder="e.g., 1"
                      style={inputStyle(saving)}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
                      Period Label *
                    </label>
                    <input
                      type="text"
                      value={formData.period_label}
                      maxLength={10}
                      onChange={(e) => setFormData({ ...formData, period_label: e.target.value })}
                      disabled={saving}
                      placeholder="e.g., P1"
                      style={inputStyle(saving)}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
                      Start Time *
                    </label>
                    <input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      disabled={saving}
                      style={inputStyle(saving)}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
                      End Time *
                    </label>
                    <input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      disabled={saving}
                      style={inputStyle(saving)}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
                    Slot Type *
                  </label>
                  <select
                    value={formData.slot_type}
                    onChange={(e) => setFormData({ ...formData, slot_type: e.target.value })}
                    disabled={saving}
                    style={inputStyle(saving)}
                  >
                    {SLOT_TYPE_SELECT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={closeForm}
                    disabled={saving}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '10px', border: '1.5px solid #e2e8f0',
                      background: 'white', color: '#475569', fontSize: '13px', fontWeight: 700,
                      cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveForm}
                    disabled={saving}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                      color: 'white', fontSize: '13px', fontWeight: 700,
                      cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                    }}
                  >
                    {saving ? 'Saving…' : editingSlot ? 'Update' : 'Add'}
                  </button>
                </div>
              </div>
            )}

            {/* ── List ── */}
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: '16px' }}>
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>Loading time slots...</span>
              </div>
            ) : sortedSlots.length === 0 && !formOpen ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '10px' }}>
                <i className="pi pi-inbox" style={{ fontSize: '32px', color: '#cbd5e1' }} />
                <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: 600 }}>No time slots found</span>
              </div>
            ) : sortedSlots.length > 0 ? (
              <div style={{
                background: 'white', borderRadius: '16px', padding: '8px',
                border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}>
                {sortedSlots.map((slot, idx) => {
                  const style = getSlotTypeStyle(slot.slot_type);
                  const isConfirming = confirmDeleteId === slot.id;

                  return (
                    <div
                      key={slot.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '14px',
                        padding: '12px 10px',
                        borderBottom: idx === sortedSlots.length - 1 ? 'none' : '1px solid #f1f5f9',
                      }}
                    >
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                        background: style.bg, color: style.color, fontWeight: 700, fontSize: '13px',
                        border: `1.5px solid ${style.border}`,
                      }}>
                        {slot.period_number}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#1f2937', marginBottom: '2px' }}>
                          {slot.period_label}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                          {formatTime(slot.start_time)} – {formatTime(slot.end_time)}
                        </div>
                      </div>

                      <span style={{
                        padding: '4px 12px', borderRadius: '7px',
                        border: `2px solid ${style.border}`,
                        background: style.bg, color: style.color,
                        fontSize: '11px', fontWeight: 600,
                        textTransform: 'capitalize', flexShrink: 0,
                      }}>
                        {slot.slot_type.replace('_', ' ')}
                      </span>

                      {isConfirming ? (
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            disabled={deleting}
                            style={{
                              padding: '6px 10px', borderRadius: '7px', border: '1px solid #e2e8f0',
                              background: 'white', color: '#475569', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                            }}
                          >
                            No
                          </button>
                          <button
                            onClick={() => handleConfirmDelete(slot.id)}
                            disabled={deleting}
                            style={{
                              padding: '6px 10px', borderRadius: '7px', border: 'none',
                              background: '#dc2626', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                            }}
                          >
                            {deleting ? '…' : 'Delete'}
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                          <button
                            onClick={() => openEditForm(slot)}
                            title="Edit"
                            style={{
                              width: '28px', height: '28px', borderRadius: '7px', border: '1px solid rgba(59,130,246,0.25)',
                              background: 'rgba(59,130,246,0.08)', color: '#2563eb', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <i className="pi pi-pencil" style={{ fontSize: '11px' }} />
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(slot.id)}
                            title="Delete"
                            style={{
                              width: '28px', height: '28px', borderRadius: '7px', border: '1px solid rgba(239,68,68,0.25)',
                              background: 'rgba(239,68,68,0.08)', color: '#dc2626', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <i className="pi pi-trash" style={{ fontSize: '11px' }} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </>
        )}
      </Dialog>
    </>
  );
};

const inputStyle = (disabled: boolean): React.CSSProperties => ({
  width: '100%', padding: '9px 11px', borderRadius: '9px',
  border: '1.5px solid #e2e8f0', fontSize: '13.5px', color: '#1e293b',
  background: disabled ? '#f1f5f9' : 'white', outline: 'none', fontFamily: 'inherit',
});

export default ViewTimeSlotsModal;